import { useEffect, useState, useCallback } from "react";
import { api, formatApiErrorDetail, isoToDisplay, displayToIso, teerTodayDisplay } from "../../lib/api";
import { ADMIN_GAMES, DEFAULT_GAME } from "../../lib/games";
import AdminLayout from "../AdminLayout";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Loader2, X } from "lucide-react";

const empty = { date: "", game: DEFAULT_GAME, fr: "", sr: "", text: "", published: true };

export default function ManageResult() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedGame, setSelectedGame] = useState(DEFAULT_GAME);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showForm, setShowForm] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/results?game=${selectedGame}`);
      setItems(data);
    } finally {
      setLoading(false);
    }
  }, [selectedGame]);

  useEffect(() => { load(); }, [load]);

  const openAdd = () => {
    setForm({ ...empty, game: selectedGame, date: teerTodayDisplay() });
    setEditId(null);
    setShowForm(true);
  };

  const openEdit = (row) => {
    setForm({ date: row.date, game: row.game || DEFAULT_GAME, fr: row.fr, sr: row.sr, text: row.text || "", published: row.published });
    setEditId(row.id);
    setShowForm(true);
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editId) {
        await api.put(`/admin/results/${editId}`, form);
        toast.success("Result updated");
      } else {
        await api.post("/admin/results", form);
        toast.success("Result published");
      }
      setShowForm(false);
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this result? It will also be removed from previous results.")) return;
    try {
      await api.delete(`/admin/results/${id}`);
      toast.success("Result deleted");
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  return (
    <AdminLayout title="Manage Result" testid="admin-manage-result">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-2">
          <label className="text-sm font-semibold text-slate-700">Game:</label>
          <select
            value={selectedGame}
            onChange={(e) => setSelectedGame(e.target.value)}
            data-testid="game-filter"
            className="rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-600"
          >
            {ADMIN_GAMES.map((g) => (
              <option key={g.slug} value={g.slug}>{g.name}</option>
            ))}
          </select>
        </div>
        <button onClick={openAdd} data-testid="add-result-btn" className="inline-flex items-center gap-1.5 rounded-lg bg-red-600 px-4 py-2 text-sm font-semibold text-white hover:bg-red-700 transition-colors shrink-0">
          <Plus size={16} /> Add Result
        </button>
      </div>

      {showForm && (
        <form onSubmit={save} className="bg-white rounded-2xl border border-slate-200 p-5 mb-6 shadow-sm" data-testid="result-form">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-oswald text-lg font-bold text-slate-900">{editId ? "Edit Result" : "Add Result"}</h2>
            <button type="button" onClick={() => setShowForm(false)} className="text-slate-400 hover:text-slate-700"><X size={20} /></button>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="sm:col-span-3">
              <label className="block text-sm font-semibold text-slate-700 mb-1">Game</label>
              <select value={form.game} onChange={(e) => setForm({ ...form, game: e.target.value })} data-testid="result-game" className="w-full rounded-lg border border-slate-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-red-600">
                {ADMIN_GAMES.map((g) => (<option key={g.slug} value={g.slug}>{g.name}</option>))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-1">Date</label>
              <input type="date" required value={displayToIso(form.date)} onChange={(e) => setForm({ ...form, date: isoToDisplay(e.target.value) })} data-testid="result-date" className="w-full rounded-lg border border-slate-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-red-600" />
            </div>
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-1">FR Number</label>
              <input type="text" value={form.fr} onChange={(e) => setForm({ ...form, fr: e.target.value })} data-testid="result-fr" placeholder="e.g. 47" className="w-full rounded-lg border border-slate-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-red-600" />
            </div>
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-1">SR Number</label>
              <input type="text" value={form.sr} onChange={(e) => setForm({ ...form, sr: e.target.value })} data-testid="result-sr" placeholder="e.g. 83" className="w-full rounded-lg border border-slate-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-red-600" />
            </div>
          </div>
          <div className="mt-4">
            <label className="block text-sm font-semibold text-slate-700 mb-1">Optional Text</label>
            <input type="text" value={form.text} onChange={(e) => setForm({ ...form, text: e.target.value })} data-testid="result-text" placeholder="Any announcement (optional)" className="w-full rounded-lg border border-slate-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-red-600" />
          </div>
          <label className="mt-4 flex items-center gap-2 text-sm font-medium text-slate-700">
            <input type="checkbox" checked={form.published} onChange={(e) => setForm({ ...form, published: e.target.checked })} data-testid="result-published" className="w-4 h-4 accent-red-600" />
            Published (visible on website)
          </label>
          <div className="mt-5 flex gap-3">
            <button type="submit" disabled={saving} data-testid="save-result-btn" className="inline-flex items-center gap-2 rounded-lg bg-red-600 px-5 py-2 text-sm font-semibold text-white hover:bg-red-700 disabled:opacity-60">
              {saving ? <Loader2 className="animate-spin" size={16} /> : null}
              {editId ? "Update & Publish" : "Publish Result"}
            </button>
            <button type="button" onClick={() => setShowForm(false)} className="rounded-lg border border-slate-300 px-5 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50">Cancel</button>
          </div>
        </form>
      )}

      <div className="overflow-x-auto w-full bg-white rounded-xl border border-slate-200">
        <table className="w-full text-left border-collapse">
          <thead>
            <tr className="bg-slate-50 border-b border-slate-200">
              <th className="px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Date</th>
              <th className="px-4 py-3 text-xs font-semibold text-slate-500 uppercase text-center">FR</th>
              <th className="px-4 py-3 text-xs font-semibold text-slate-500 uppercase text-center">SR</th>
              <th className="px-4 py-3 text-xs font-semibold text-slate-500 uppercase text-center">Status</th>
              <th className="px-4 py-3 text-xs font-semibold text-slate-500 uppercase text-right">Actions</th>
            </tr>
          </thead>
          <tbody data-testid="results-admin-table">
            {loading ? (
              <tr><td colSpan={5} className="px-4 py-8 text-center text-slate-500">Loading...</td></tr>
            ) : items.length === 0 ? (
              <tr><td colSpan={5} className="px-4 py-8 text-center text-slate-500">No results yet. Add one above.</td></tr>
            ) : (
              items.map((row) => (
                <tr key={row.id} className="border-b border-slate-100">
                  <td className="px-4 py-3 text-sm font-medium text-slate-700 whitespace-nowrap">{row.date}</td>
                  <td className="px-4 py-3 text-center font-oswald text-lg font-bold text-red-600">{row.fr || "--"}</td>
                  <td className="px-4 py-3 text-center font-oswald text-lg font-bold text-red-600">{row.sr || "--"}</td>
                  <td className="px-4 py-3 text-center">
                    <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-semibold ${row.published ? "bg-green-100 text-green-700" : "bg-slate-100 text-slate-500"}`}>
                      {row.published ? "Published" : "Draft"}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-2">
                      <button onClick={() => openEdit(row)} data-testid={`edit-result-${row.id}`} className="p-2 rounded-lg text-slate-500 hover:bg-slate-100 hover:text-red-600"><Pencil size={16} /></button>
                      <button onClick={() => remove(row.id)} data-testid={`delete-result-${row.id}`} className="p-2 rounded-lg text-slate-500 hover:bg-red-50 hover:text-red-600"><Trash2 size={16} /></button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </AdminLayout>
  );
}
